import {captureException} from '@sentry/vue'
import {i18n} from '@toolify/client/src/setup/i18n'
import {useLocalMessageStore} from '@toolify/client/src/stores/LocalMessageStore/useLocalMessageStore'
import {IMessagesApi} from '@toolify/client/src/modules/notifications/types/IMessagesApi'
import {IMessageInput} from '@toolify/client/src/modules/notifications/types/IMessageInput'
import {IMessageAction} from '@toolify/client/src/modules/notifications/types/IMessageAction'
import {IMessageProgressHandle} from '@toolify/client/src/modules/notifications/types/IMessageProgressHandle'
import {MessageSeverityType} from '@toolify/client/src/modules/notifications/enum/MessageSeverityType'
import {MessageActionKindType} from '@toolify/client/src/modules/notifications/enum/MessageActionKindType'
import {MessageDismissReasonType} from '@toolify/client/src/modules/notifications/enum/MessageDismissReasonType'
import {omitUndefinedFields} from '@toolify/client/src/modules/notifications/functions/omitUndefinedFields'

const SUCCESS_DURATION_MS = 3000
const INFO_DURATION_MS = 4000
const WARNING_DURATION_MS = 5000
const ERROR_DURATION_MS = 6000
const UNDO_DURATION_MS = 6000
const UNDO_WITH_TITLE_DURATION_MS = 8000
const UNDONE_DURATION_MS = 3000

// Messages API for local (client-side) cards. The store is resolved inside every call, so
// `const messages = useMessages()` also works at module scope and inside other stores.
export function useMessages(): IMessagesApi {
  const getStore = () => useLocalMessageStore()

  function push(input: IMessageInput): string {
    return getStore().push(input)
  }

  function dismiss(id: string, reason = MessageDismissReasonType.Api): boolean {
    return getStore().dismiss(id, reason)
  }

  // an explicit `undefined` in the options must not override the shortcut's own default
  function getOptions<T extends object>(options: T | undefined): T {
    return omitUndefinedFields(options ?? {} as T)
  }

  const success: IMessagesApi['success'] = (text, options) => {
    return push({
      duration: SUCCESS_DURATION_MS,
      ...getOptions(options),
      severity: MessageSeverityType.Success,
      text,
    })
  }

  const info: IMessagesApi['info'] = (text, options) => {
    return push({
      duration: INFO_DURATION_MS,
      ...getOptions(options),
      severity: MessageSeverityType.Info,
      text,
    })
  }

  const warning: IMessagesApi['warning'] = (text, options) => {
    return push({
      duration: WARNING_DURATION_MS,
      ...getOptions(options),
      severity: MessageSeverityType.Warning,
      text,
    })
  }

  // With an error id the card stays until closed, so the id can be copied for support
  const error: IMessagesApi['error'] = (text, rawOptions) => {
    const options = getOptions(rawOptions)
    if(options.errorId) {
      return push({
        duration: 0,
        pinned: true,
        ...options,
        severity: MessageSeverityType.Error,
        text,
      })
    }
    return push({
      duration: ERROR_DURATION_MS,
      ...options,
      severity: MessageSeverityType.Error,
      text,
    })
  }

  const undo: IMessagesApi['undo'] = (text, onUndo, options) => {
    const {title, undoneText, undoTooltip} = getOptions(options)
    let isUndoing = false
    const undoAction: IMessageAction = {
      label: i18n.global.t('general.undo'),
      kind: MessageActionKindType.Undo,
      closes: false,
      ...(undoTooltip ? {tooltip: undoTooltip} : {}),
    }
    return push({
      duration: title ? UNDO_WITH_TITLE_DURATION_MS : UNDO_DURATION_MS,
      timerBar: true,
      title,
      severity: MessageSeverityType.Success,
      text,
      actions: [undoAction],
      onUndo: async context => {
        if(isUndoing) {
          return
        }
        isUndoing = true
        try {
          await onUndo()
        } catch (undoError) {
          // nobody awaits onUndo: report the failure here and turn the card into an error
          captureException(undoError)
          const failurePatch = {
            severity: MessageSeverityType.Error,
            title: '',
            text: i18n.global.t('notifications.messages.undoFailed'),
            actions: [],
            duration: ERROR_DURATION_MS,
            timerBar: false,
          }
          if(!context.update(failurePatch)) {
            push(failurePatch)
          }
          return
        }
        if(!undoneText) {
          context.dismiss(MessageDismissReasonType.Action)
          return
        }
        context.update({
          title: '',
          text: undoneText,
          actions: [],
          duration: UNDONE_DURATION_MS,
          timerBar: false,
        })
      },
    })
  }

  const progress: IMessagesApi['progress'] = (title, options) => {
    const {value = 0, total = 100, label, indeterminate = false, ...rest} = getOptions(options)
    const id = push({
      dismissible: false,
      pinned: true,
      ...rest,
      severity: MessageSeverityType.Progress,
      duration: 0,
      title,
      progress: {
        value,
        total,
        label,
        indeterminate,
      },
    })

    const handle: IMessageProgressHandle = {
      id,
      set: (nextValue, nextLabel, nextText) => getStore().setProgress(id, nextValue, nextLabel, nextText),
      succeed: (result = {}) => getStore().update(id, {
        severity: MessageSeverityType.Success,
        progress: null,
        pinned: false,
        dismissible: true,
        duration: SUCCESS_DURATION_MS,
        actions: [],
        ...result,
      }),
      fail: (result = {}) => getStore().update(id, {
        severity: MessageSeverityType.Error,
        progress: null,
        pinned: true,
        dismissible: true,
        duration: 0,
        actions: [],
        ...result,
      }),
      dismiss: () => dismiss(id, MessageDismissReasonType.Api),
    }
    return handle
  }

  return {
    dismiss,
    success,
    info,
    warning,
    error,
    undo,
    progress,
  }
}
